import React, { useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';

import { noop, layout } from '@prisjakt/ui-base';
import T from '@prisjakt/ui-translate';

import ItemRow from '../item-row';
import ProductRow from '../product-row';
import EditControl from '../edit-control';

import { Item, EditFunctions, ItemWithProduct, Deleted } from '../sharedTypes';

import * as S from './styles';

export type ListItemProps = {
  item: Item;
  deleted?: Deleted;
  onClick: (id: number) => void;
  draggable: boolean;
  hasDragHandle?: boolean;
} & EditFunctions;

const withLink = (to: string | undefined, component: React.ReactNode) =>
  to ? <Link to={to}>{component}</Link> : component;

const ListItem = (props: ListItemProps) => {
  const {
    item,
    deleted,
    onClick,
    createAlert,
    onEdit,
    onDelete,
    updateQuantity,
    hasDragHandle,
  } = props;

  const canEdit = item.__typename !== 'AlertSearch';
  const editControl = useRef<HTMLDivElement>(null);

  const editControlElement = (
    <div ref={editControl}>
      <EditControl
        canEdit={canEdit}
        item={item}
        onDelete={onDelete}
        createAlert={createAlert}
        onEdit={onEdit}
      />
    </div>
  );

  const renderListItem = () => {
    switch (item.__typename) {
      case 'AlertProduct':
        if (!item.product) {
          return (
            <S.NotFound>
              <T domain="alerts">list.no-product-found</T>
            </S.NotFound>
          );
        }
        return withLink(
          item.product.pathName,
          <ProductRow item={item as ItemWithProduct} />
        );
      case 'AlertSearch':
        if (!item.search) {
          return (
            <S.NotFound>
              <T domain="alerts">list.no-search-found</T>
            </S.NotFound>
          );
        }

        return withLink(
          item.search.pathName,
          <ItemRow
            name={item.search.name}
            type={item.__typename}
            subtitle={
              <T domain="alerts" n={item.search.productsCount}>
                list.n-products-found
              </T>
            }
          />
        );
      case 'ListProduct':
        return item.product ? (
          withLink(
            item.product.pathName,
            <ProductRow
              item={item as ItemWithProduct}
              updateQuantity={updateQuantity}
            />
          )
        ) : (
          <S.NotFound>
            <T domain="alerts">list.no-product-found</T>
          </S.NotFound>
        );
      case 'ListSearch':
        if (!item.search) {
          return (
            <S.NotFound>
              <T domain="alerts">list.no-search-found</T>
            </S.NotFound>
          );
        }

        return withLink(
          item.search.pathName,
          <ItemRow
            name={item.search.name}
            type={item.__typename}
            subtitle={
              <T domain="lists" n={item.search.productsCount}>
                list.n-products
              </T>
            }
          />
        );

      case 'ListCategory':
        return (
          <ItemRow
            name={item.name}
            icon={item.icon}
            type={item.__typename}
            subtitle={
              <T domain="lists" n={item.itemsCount}>
                list.n-items
              </T>
            }
          />
        );
      default:
        return null;
    }
  };

  const handleClick = useCallback(
    e => {
      // Dont nagivate to list/alert if clicking the edit button
      if (editControl.current && editControl.current.contains(e.target)) return;

      onClick(item.id);
    },
    [item.id, onClick]
  );

  const listItemStyling = ['ListCategory'].includes(item.__typename)
    ? 'background'
    : 'lines';

  return (
    <S.ListItem
      styling={listItemStyling}
      deleting={deleted ? Boolean(deleted[item.id]) : false}
      onClick={handleClick}
      draggable={props.draggable}
      hasDragHandle={hasDragHandle}
    >
      {props.draggable && !hasDragHandle && (
        <S.DragIcon size={layout.dragAndDropHandleSize.desktop.value} />
      )}
      <S.Content>{renderListItem()}</S.Content>
      <S.EditControl absolutePositioned={item.__typename !== 'ListCategory'}>
        {editControlElement}
      </S.EditControl>
    </S.ListItem>
  );
};

ListItem.defaultProps = {
  onClick: noop,
  draggable: false,
};

export default ListItem;
